import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { API, getCached, setCached } from "./cache";
import UserView from "./features/dashboard/components/UserView";
import WhatsNew from "./features/dashboard/components/WhatsNew";
import MonitoringTab from "./features/dashboard/components/MonitoringTab";
import { AlertWidgetContext } from "./App";
import { FilterTriggerButton } from "./features/common/components";
import ProjectPulseTab from "./features/dashboard/components/ProjectPulseTab";
import UtilizationTab from "./features/dashboard/components/UtilizationTab";
import { STATUS_COLORS } from "./features/dashboard/constants/statusColors";

const TEAM_COLORS = [
  "#004d40","#1b5e20","#4a148c","#bf360c",
  "#1a237e","#880e4f","#e65100","#01579b",
];

const COLS = [
  { key: "customer", label: "Customer",    width: 200, group: "info" },
  { key: "project",  label: "Project",     width: 260, group: "info" },
  { key: "total",    label: "Total Tasks", width: 120, group: "info", type: "num" },
];

function SummaryCard({ label, value, sub, color, onClick }) {
  return (
    <div
      className={`summary-card${onClick ? " summary-card--clickable" : ""}`}
      style={{
        borderTop: `4px solid ${color}`,
        background: `linear-gradient(145deg, ${color}26 0%, #ffffff 60%)`,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      <div className="summary-card__label">{label}</div>
      <div className="summary-card__value" style={{ color }}>{value}</div>
      {sub && <div className="summary-card__sub">{sub}</div>}
    </div>
  );
}

function NoTasksPopup({ users, onClose }) {
  const overlayRef = useRef(null);
  return (
    <div
      className="dts-overlay"
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="db-no-tasks-panel">
        <div className="db-no-tasks-panel__header">
          <div>
            <div className="db-no-tasks-panel__title">Users Without Active Tasks</div>
            <div className="db-no-tasks-panel__sub">{users.length} user{users.length !== 1 ? "s" : ""} with no open/in-progress tasks</div>
          </div>
          <button className="db-no-tasks-panel__close" onClick={onClose}>×</button>
        </div>
        <div className="db-no-tasks-panel__body">
          {users.length === 0 ? (
            <div className="state-empty">All users have active tasks assigned.</div>
          ) : (
            <table className="db-no-tasks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Team</th>
                  <th>Lead</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id}>
                    <td className="db-no-tasks-table__num">{i + 1}</td>
                    <td>{u.display_name || "—"}</td>
                    <td>{u.team_name || <span className="db-no-tasks-table__empty">No team</span>}</td>
                    <td>{u.lead_name || <span className="db-no-tasks-table__empty">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBreakdown({ title, data, color, overdueCount }) {
  if (!data) return null;
  const statusOrder = ["Open", "In Progress", "Completed", "On Hold", "Cancelled", "Not Started"];
  const sortedEntries = statusOrder.filter(s => s in data).map(s => [s, data[s]]);

  return (
    <div className="status-breakdown" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="status-breakdown__title">{title}</div>
      <div className="status-breakdown__grid">
        {overdueCount !== undefined && (
          <div className="status-item status-item--overdue">
            <span className="status-badge-small" style={{ background: "#FFEBEE", color: "#c62828" }}>
              <span className="status-dot-small" style={{ background: "#ef5350" }} />
              Overdue
            </span>
            <span className="status-count">{overdueCount}</span>
          </div>
        )}
        {sortedEntries.map(([status, count]) => {
          const sc = STATUS_COLORS[status] || STATUS_COLORS["Not Started"];
          return (
            <div key={status} className="status-item">
              <span className="status-badge-small" style={{ background: sc.bg, color: sc.color }}>
                <span className="status-dot-small" style={{ background: sc.dot }} />
                {status}
              </span>
              <span className="status-count">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cell({ col, value }) {
  const cellStyle = { width: col.width, minWidth: col.width, maxWidth: col.width };
  if (col.type === "num") {
    return (
      <div className="cell cell--num cell--default-num" style={cellStyle}>
        {Number(value) || "—"}
      </div>
    );
  }
  return (
    <div className="cell" style={cellStyle} title={value}>
      <span className="cell__text">
        {value || <span className="cell__empty">—</span>}
      </span>
    </div>
  );
}

export default function Dashboard({ showToast, user }) {
  const isAdmin    = ["Admin", "Super Admin"].includes(user?.db_role);
  const isTeamLead = user?.is_team_lead === true;
  const isExternal = (() => {
    const t = String(user?.db_user_type || "").trim().toLowerCase();
    return t === "external" || t === "external user";
  })();
  // Admin View is temporarily hidden. Everyone lands on My View by default.
  const TABS = [
    "My View",
    ...((isAdmin || isTeamLead) ? ["Team Lead View"] : []),
    "Project Pulse",
    "Monitoring",
    ...(!isExternal ? ["Utilization"] : []),
    "What's New",
  ];
  const defaultTab = "My View";
  const [activeTab, setActiveTab]             = useState(defaultTab);
  const [projectData, setProjectData]         = useState([]);
  const [summary, setSummary]                 = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState("");

  // Utilization toolbar state (lifted so it lives in the shared page-toolbar,
  // same reasoning as the Monitoring toolbar state below)
  const [utilSearch, setUtilSearch] = useState("");

  // Monitoring toolbar state (lifted so it lives in the shared page-toolbar)
  const [monSearch,     setMonSearch]     = useState("");
  const [monCustFilter, setMonCustFilter] = useState("");
  const [monStatFilter, setMonStatFilter] = useState("");
  const [monDateFrom,   setMonDateFrom]   = useState("");
  const [monDateTo,     setMonDateTo]     = useState("");
  const [monDrawerOpen, setMonDrawerOpen] = useState(false);
  const monActiveFilterCount =
    (monCustFilter ? 1 : 0) + (monStatFilter ? 1 : 0) + (monDateFrom || monDateTo ? 1 : 0);

  const MON_REFRESH_OPTIONS = [
    { value: 0,      label: "Off"  },
    { value: 5000,   label: "5s"   },
    { value: 10000,  label: "10s"  },
    { value: 15000,  label: "15s"  },
    { value: 30000,  label: "30s"  },
    { value: 60000,  label: "1m"   },
    { value: 120000, label: "2m"   },
    { value: 180000, label: "3m"   },
    { value: 300000, label: "5m"   },
  ];

  const {
    isOpen:            widgetOpen,
    launch:            launchWidget,
    close:             closeWidget,
    monRefreshInterval,
    onMonRefreshChange: handleMonRefreshChange,
  } = useContext(AlertWidgetContext);

  // User View state
  const [userViewData, setUserViewData]       = useState(null);
  const [userViewLoading, setUserViewLoading] = useState(false);
  const [activeCard, setActiveCard]           = useState("overdue_tasks");
  const [sendingMail, setSendingMail]         = useState(false);
  const [nextSchedule, setNextSchedule]       = useState(null);

  // Team Lead View state
  const [teamLeadViewData, setTeamLeadViewData]       = useState(null);
  const [teamLeadViewLoading, setTeamLeadViewLoading] = useState(false);

  const handleUserViewRowClick = useCallback((row) => {
    const resolveTaskId = (r) =>
      r?.id || r?.task_id || r?.task_uuid || r?.taskId || r?.uuid || null;
    const resolveDefectId = (r) =>
      r?.id || r?.defect_id || r?.defect_uuid || r?.defectId || r?.uuid || null;

    if (activeCard.endsWith("_tasks")) {
      const taskId = resolveTaskId(row);
      if (!taskId) return;
      window.open(`/masterv2?openTaskId=${encodeURIComponent(taskId)}`, "_blank", "noopener,noreferrer");
      return;
    }
    if (activeCard.endsWith("_defects")) {
      const defectId = resolveDefectId(row);
      if (!defectId) return;
      window.open(`/defectsv2?openDefectId=${encodeURIComponent(defectId)}`, "_blank", "noopener,noreferrer");
    }
  }, [activeCard]);

  useEffect(() => {
    const tabFromUrl = new URLSearchParams(window.location.search).get("tab");
    if (!tabFromUrl) return;
    const norm = String(tabFromUrl).trim().toLowerCase().replace(/[\s_-]+/g, "");
    const found = TABS.find((t) => t.toLowerCase().replace(/[\s_-]+/g, "") === norm);
    if (found) setActiveTab(found);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserView = useCallback(async () => {
    setUserViewLoading(true);
    try {
      const data = await fetch(`${API}/dashboard/user-view`, { credentials: "include" }).then(r => r.json());
      setUserViewData(data);
    } catch (err) {
      console.error("User view fetch error:", err);
      showToast("Cannot load user data", "warn");
    }
    setUserViewLoading(false);
  }, [showToast]);

  const fetchTeamLeadView = useCallback(async () => {
    setTeamLeadViewLoading(true);
    try {
      const data = await fetch(`${API}/dashboard/team-lead-view`, { credentials: "include" }).then(r => r.json());
      setTeamLeadViewData(data);
    } catch (err) {
      console.error("Team lead view fetch error:", err);
      showToast("Cannot load team lead data", "warn");
    }
    setTeamLeadViewLoading(false);
  }, [showToast]);

  useEffect(() => {
    if (activeTab === "My View") fetchUserView();
    if (activeTab === "Team Lead View") fetchTeamLeadView();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const cached = getCached("customers-projects-active");
      const [consolidatedReport, customerProjects, tasks] = await Promise.all([
        fetch(`${API}/consolidated-report`, { credentials: "include" }).then(r => r.json()),
        cached ? Promise.resolve(cached) : fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/tasks`, { credentials: "include" }).then(r => r.json()),
      ]);
      const cpList    = Array.isArray(customerProjects) ? customerProjects : [];
      const tasksList = Array.isArray(tasks) ? tasks : [];
      if (!cached) setCached("customers-projects-active", cpList);

      const projectBreakdown = [];
      cpList.forEach(customer => {
        (customer.projects || []).forEach(project => {
          const count = tasksList.filter(
            f => f.customer === customer.customer_name && f.project === project.project_name
          ).length;
          projectBreakdown.push({
            customer: customer.customer_name,
            project:  project.project_name,
            total:    count,
          });
        });
      });

      setProjectData(projectBreakdown);
      setSummary(consolidatedReport);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      showToast("Cannot reach API", "warn");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API}/admin/compliance-schedule`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.next_run_ist) setNextSchedule(d.next_run_ist); })
      .catch(() => {});
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = projectData.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.customer?.toLowerCase().includes(q) || r.project?.toLowerCase().includes(q);
  });

  const totalWidth = COLS.reduce((a, c) => a + c.width, 0) + 80;

  const overallStatusCounts = {};
  let totalOverdue = 0;
  if (summary) {
    Object.values(summary.teams || {}).forEach(team => {
      Object.entries(team.status_counts).forEach(([status, count]) => {
        overallStatusCounts[status] = (overallStatusCounts[status] || 0) + count;
      });
      totalOverdue += team.overdue_count;
    });
  }

  const teamEntries = summary ? Object.entries(summary.teams || {}) : [];

  return (
    <>
      <div className="page-head">
        <h1>Dashboard <em>— overview</em></h1>
        <div className="page-head-actions">
          {isAdmin && (
            <>
              {nextSchedule && (
                <span className="db-mail-schedule">
                  <span className="k">Next Run</span>
                  <span className="k">{nextSchedule}</span>
                </span>
              )}
              <button
                className="db-mail-btn"
                disabled={sendingMail}
                onClick={async () => {
                  setSendingMail(true);
                  try {
                    const res = await fetch(`${API}/admin/send-all-compliance-reports`, {
                      method: "POST",
                      credentials: "include",
                    });
                    const data = await res.json();
                    if (res.ok) showToast(data.message || "Compliance reports queued", "success");
                    else showToast(data.detail || "Failed to start reports", "warn");
                  } catch {
                    showToast("Could not reach API", "warn");
                  }
                  setSendingMail(false);
                }}
              >
                {sendingMail ? "Sending…" : "✉ Send Compliance Reports"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`page-tab${activeTab === tab ? " page-tab--active" : ""}`}
              onClick={() => {
                setActiveTab(tab);
                const slug = tab.toLowerCase().replace(/\s+/g, "-");
                const url = new URL(window.location.href);
                url.searchParams.set("tab", slug);
                window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}`);
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        {activeTab === "Monitoring" && (
          <div className="page-toolbar-right">
            <button
              className={`mon-widget-btn${widgetOpen ? " mon-widget-btn--active" : ""}`}
              onClick={() => widgetOpen ? closeWidget() : launchWidget(showToast)}
              title={widgetOpen ? "Close desktop widget" : "Open floating desktop widget"}
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <rect x="12" y="3" width="10" height="7" rx="1" fill="currentColor" stroke="none" />
              </svg>
              {widgetOpen ? "Close Widget" : "Widget"}
            </button>
            <div className="mon-toolbar-search">
              <svg className="mon-toolbar-search__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className="mon-toolbar-search__input"
                type="text"
                placeholder="Search alert name…"
                value={monSearch}
                onChange={e => setMonSearch(e.target.value)}
              />
            </div>
            <div className={`mon-toolbar-refresh${monRefreshInterval > 0 ? " mon-toolbar-refresh--active" : ""}`}>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              <select
                className="mon-toolbar-refresh__select"
                value={monRefreshInterval}
                onChange={e => handleMonRefreshChange(Number(e.target.value))}
              >
                {MON_REFRESH_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <FilterTriggerButton
              activeCount={monActiveFilterCount}
              onClick={() => setMonDrawerOpen(true)}
              onClear={() => { setMonCustFilter(""); setMonStatFilter(""); setMonDateFrom(""); setMonDateTo(""); }}
            />
          </div>
        )}
        {activeTab === "Utilization" && (
          <div className="page-toolbar-right">
            <div className="mon-toolbar-search">
              <svg className="mon-toolbar-search__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className="mon-toolbar-search__input"
                type="text"
                placeholder="Search epic, customer, code…"
                value={utilSearch}
                onChange={e => setUtilSearch(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* My View */}
      {activeTab === "My View" && (
        <UserView
          data={userViewData}
          loading={userViewLoading}
          activeCard={activeCard}
          setActiveCard={setActiveCard}
          onRowClick={handleUserViewRowClick}
        />
      )}

      {/* Team Lead View */}
      {activeTab === "Team Lead View" && (
        <UserView
          data={teamLeadViewData}
          loading={teamLeadViewLoading}
          activeCard={activeCard}
          setActiveCard={setActiveCard}
          includeAssignee
          onRowClick={handleUserViewRowClick}
        />
      )}

      {/* Project Pulse */}
      {activeTab === "Project Pulse" && (
        <ProjectPulseTab user={user} showToast={showToast} />
      )}

      {/* Monitoring */}
      {activeTab === "Monitoring" && (
        <MonitoringTab
          user={user}
          search={monSearch}
          custFilter={monCustFilter}
          statFilter={monStatFilter}
          dateFrom={monDateFrom}
          dateTo={monDateTo}
          drawerOpen={monDrawerOpen}
          refreshInterval={monRefreshInterval}
          onDrawerClose={() => setMonDrawerOpen(false)}
          onFilterApply={(cust, stat, from, to) => {
            setMonCustFilter(cust);
            setMonStatFilter(stat);
            setMonDateFrom(from);
            setMonDateTo(to);
          }}
          onCustomerClick={(id) => setMonCustFilter(id || "")}
        />
      )}

      {/* Utilization */}
      {activeTab === "Utilization" && (
        <UtilizationTab user={user} showToast={showToast} search={utilSearch} />
      )}

      {/* What's New */}
      {activeTab === "What's New" && (
        <WhatsNew user={user} />
      )}

      {/* Admin View — summary cards (only when data loaded) */}
      {activeTab === "Admin View" && summary && (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            {["Admin", "Super Admin"].includes(user?.db_role) && (
              <SummaryCard label="Total Customers" value={summary.total_customers} color="#1a237e" />
            )}
            {["Admin", "Super Admin"].includes(user?.db_role) && (
              <SummaryCard label="Total Projects"  value={summary.total_projects}  color="#2e7d32" />
            )}
            <SummaryCard label="Total Tasks"     value={summary.total_tasks}     color="#1565c0" />
            {teamEntries.map(([teamName, stats], i) => (
              <SummaryCard
                key={teamName}
                label={`${teamName} Tasks`}
                value={stats.total_applicable}
                color={TEAM_COLORS[i % TEAM_COLORS.length]}
                sub={`⚠ ${stats.overdue_count} overdue`}
              />
            ))}
          </div>

          {/* Overall Status Distribution */}
          <div className="status-overview">
            <h3 className="status-overview__title">Overall Status Distribution</h3>
            <div className="status-overview__grid">
              <div className="status-overview__item">
                <div className="status-overview__badge" style={{ background: "#FFEBEE", color: "#c62828" }}>
                  <span className="status-dot" style={{ background: "#ef5350" }} />
                  Overdue
                </div>
                <div className="status-overview__count">{totalOverdue}</div>
              </div>
              {["Open","In Progress","Completed","On Hold","Cancelled","Not Started"]
                .filter(s => overallStatusCounts[s] !== undefined)
                .map(status => {
                  const sc = STATUS_COLORS[status] || STATUS_COLORS["Not Started"];
                  return (
                    <div key={status} className="status-overview__item">
                      <div className="status-overview__badge" style={{ background: sc.bg, color: sc.color }}>
                        <span className="status-dot" style={{ background: sc.dot }} />
                        {status}
                      </div>
                      <div className="status-overview__count">{overallStatusCounts[status]}</div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Team Status Breakdowns */}
          {teamEntries.length > 0 && (
            <div className="module-breakdowns">
              {teamEntries.map(([teamName, stats], i) => (
                <StatusBreakdown
                  key={teamName}
                  title={`${teamName} Status`}
                  data={stats.status_counts}
                  color={TEAM_COLORS[i % TEAM_COLORS.length]}
                  overdueCount={stats.overdue_count}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Admin View — toolbar and table (always visible on this tab) */}
      {activeTab === "Admin View" && (
        <>
          <div className="toolbar">
            <input
              className="toolbar__search"
              placeholder="🔍  Search customer, project…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="toolbar__spacer" />
            <span className="toolbar__count">{filtered.length} projects</span>
          </div>

          <div className="table-wrap" style={{ flex: "none", overflow: "visible", height: "auto" }}>
            <div className="table-inner" style={{ minWidth: totalWidth }}>
              <div className="col-header-row" style={{ top: 0 }}>
                <div className="col-header-num"><span>#</span></div>
                {COLS.map(col => (
                  <div
                    key={col.key}
                    className="col-header-cell"
                    style={{ width: col.width, minWidth: col.width, textAlign: col.type === "num" ? "center" : "left" }}
                  >
                    {col.label}
                  </div>
                ))}
              </div>

              {loading && <div className="state-loading">Loading…</div>}

              {!loading && filtered.map((row, idx) => (
                <div key={`${row.customer}-${row.project}`} className="data-row">
                  <div className="row-meta">
                    <span className="row-num">{idx + 1}</span>
                  </div>
                  {COLS.map(col => (
                    <Cell key={col.key} col={col} value={row[col.key]} />
                  ))}
                </div>
              ))}

              {!loading && filtered.length === 0 && (
                <div className="state-empty">No projects found. Try adjusting filters.</div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
